import { IVectorLayerGroupConfig } from "@esdm/map/dist/layers/groups/vector/interfaces/IVectorLayerGroupConfig";
import { WfsParamSourceMapServer, IWfsParamSource } from "@esdm/map/dist/layers/vector/sources/wfs";
import { VectorLayerStrategy } from "@esdm/map/dist/layers/vector/VectorLayerStrategy";

import { DerbyshireRecordType, DerbyshireRecordSubType} from "./DerbyshireRecordTypes";
import { FeatureAdd } from "@esdm/map/dist/layers/vector/sources/loaders";

export class DefaultLayerConfigurationFactory {

    private mapFileUrl: string = "/api/MapServer/Hbsmr";

    private paramSource(): IWfsParamSource {
        return new WfsParamSourceMapServer(null,{srsName: "EPSG:27700"});
    }

    constructor() {
    }

    public layerConfig = (
        recordType: DerbyshireRecordType,
        recordSubType: DerbyshireRecordSubType = null,
        opts: IVectorLayerGroupConfig = {}
    ): IVectorLayerGroupConfig => {
        let layerConfig: IVectorLayerGroupConfig = null;

        switch (recordType) {
            case DerbyshireRecordType.Monument:
                layerConfig = this.Monument(opts);
                break;
            case DerbyshireRecordType.Paleochannels:
                layerConfig = this.Paleochannels(opts);
                break;
        }

        if (layerConfig === null && recordSubType !== null) {
            switch (recordSubType) {

                case DerbyshireRecordSubType.ListedBuilding:
                    layerConfig = this.ListedBuilding(opts);
                    break;

                case DerbyshireRecordSubType.ScheduledMonument:
                    layerConfig = this.ScheduledMonument(opts);
                    break;    
                                
                case DerbyshireRecordSubType.RegisteredParkAndGarden:
                    layerConfig = this.RegisteredParkAndGarden(opts);
                    break; 

                case DerbyshireRecordSubType.ConservationArea:
                    layerConfig = this.ConservationArea(opts);
                    break; 

                case DerbyshireRecordSubType.AreaOfArchaeologicalInterest:
                    layerConfig = this.AreaOfArchaeologicalInterest(opts);
                    break; 

                case DerbyshireRecordSubType.WorldHeritageSite:
                    layerConfig = this.WorldHeritageSite(opts);
                    break; 

                case DerbyshireRecordSubType.WorldHeritageSiteBufferZone:
                    layerConfig = this.WorldHeritageSiteBufferZone(opts);
                    break; 

            }
        }

        return layerConfig;
    }

    private Monument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Monuments",
                ref: "Monuments",
                type: DerbyshireRecordType.Monument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 2000,
                //legend: { point: false, line: false },
                visibleOnFirstLoad: true,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "MonumentPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentLinesAsPointsWFS",
                        minZoom: 0,
                        maxZoom: 0
                    },
                    {
                        layerName: "MonumentPolygonsAsPointsWFS",
                        minZoom: 0,
                        maxZoom: 0
                    }
                ]
            }, ...opts
        };
    }


    private ListedBuilding = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Listed Buildings",
                ref: "ListedBuildings",
                type: DerbyshireRecordType.Designation,
                subType: DerbyshireRecordSubType.ListedBuilding,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: DerbyshireRecordSubType.ListedBuilding },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: false,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 16,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 15
                    }
                ]
            }, ...opts
        };
    }

    private ScheduledMonument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Scheduled Monuments",
                ref: "ScheduledMonuments",
                type: DerbyshireRecordType.Designation,
                subType: DerbyshireRecordSubType.ScheduledMonument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: DerbyshireRecordSubType.ScheduledMonument },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },                   
                   
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }
 
    private RegisteredParkAndGarden = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Registered Parks and Gardens",
                ref: "RegisteredParksAndGardens",
                type: DerbyshireRecordType.Designation,
                subType: DerbyshireRecordSubType.RegisteredParkAndGarden,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: DerbyshireRecordSubType.RegisteredParkAndGarden },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },                                     
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 0,
                        maxZoom: 0
                    }
                ]
            }, ...opts
        };
    }

    private ConservationArea = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Conservation Areas",
                ref: "ConservationArea",
                type: DerbyshireRecordType.Designation,
                subType: DerbyshireRecordSubType.ConservationArea,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: DerbyshireRecordSubType.ConservationArea },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 0,
                        maxZoom: 0
                    }
                ]
            }, ...opts
        };
    }


    private AreaOfArchaeologicalInterest = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Areas of Archaeological Interest",
                ref: "AreaOfArchInterest",
                type: DerbyshireRecordType.Designation,
                subType: DerbyshireRecordSubType.AreaOfArchaeologicalInterest,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: DerbyshireRecordSubType.AreaOfArchaeologicalInterest },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 0,
                        maxZoom: 0
                    }
                ]
            }, ...opts
        };
    }


    private WorldHeritageSite = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "World Heritage Site",
                ref: "WorldHeritageSite",
                type: DerbyshireRecordType.Designation,
                subType: DerbyshireRecordSubType.WorldHeritageSite,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: DerbyshireRecordSubType.WorldHeritageSite },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 0,
                        maxZoom: 0
                    }
                ]
            }, ...opts
        };
    }


    private WorldHeritageSiteBufferZone = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "World Heritage Site Buffer Zone",
                ref: "WorldHeritageSiteBufferZone",
                type: DerbyshireRecordType.Designation,
                subType: DerbyshireRecordSubType.WorldHeritageSiteBufferZone,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: DerbyshireRecordSubType.WorldHeritageSiteBufferZone },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 0,
                        maxZoom: 0
                    }
                ]
            }, ...opts
        };
    }

    private Paleochannels = (
            opts: IVectorLayerGroupConfig
        ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Paleochannels",
                ref: "Paleochannels",
                type: DerbyshireRecordType.Paleochannels,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 2000,
                legend: { point: false, line: false },
                overrideFeatureAdd: new FeatureAdd(),
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                queryable: false,
                layers: [
                    {
                        layerName: "PaleochannelsWFS",
                        minZoom: 0,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

}

